<template>
    <div :class="[{ 'table-custom-search': customSearch }, 'table-responsive']">
        <slot/>

        <div
            v-if="isLoading"
            class="spinner-holder"
        >
            <ui-spinner />
        </div>

        <table ref="table" class="w-100">
            <thead>
                <tr>
                    <th
                        v-for="(column, index) in columns"
                        :key="index"
                    >
                        <template v-if="column.name">
                            {{ column.title }}
                        </template>

                        <template v-if="column.legend">
                            {{ lang.get(`document.table.head.${column.name}`) }}
                            <span
                                class="ff-icon-info ml-1"
                                :title="legendHTML"
                                data-tooltip
                            />
                        </template>
                    </th>
                </tr>
            </thead>
            <tbody></tbody>
        </table>
    </div>
</template>

<script>
    import { mapState, mapMutations, mapGetters } from 'vuex';
    import 'datatables.net';
    import $ from 'jquery';
    import filesize from 'filesize';
    import tippy from 'tippy.js';

    import UiSpinner from '../ui/spinner.vue';

    export default {
        name: 'UiDataTable',
        components: {
            UiSpinner,
        },
        data() {
            return {
                dataTable: null,
                filterDebounce: null,
                isInited: false,
                isLoading: true,
                legendHTML: null,
                query: {
                    page: this.page,
                },
                table: null,
            };
        },
        props: {
            ajax: {
                required: false,
                type: String,
                default: null,
            },
            minWidth: {
                required: false,
                type: String,
                default: '1000px',
            },
            columns: {
                required: true,
                type: Array,
            },
            checkValueById: {
                required: false,
                type: Boolean,
                default: true,
            },
            customSearch: {
                type: Boolean,
                default: false,
            },
            order: {
                type: Array,
                default: [1, 'asc'],
            },
            language: {
                type: Object,
                default: () => {},
            },
            link: {
                required: false,
                type: String,
                default: '',
            },
            page: {
                type: [Number,String],
                default: 0,
            },
            pageLength: {
                type: [Number,String],
                default: 25,
            },
            rowClickable: {
                type: Boolean,
                default: false,
            },
            unreadRowClass: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            ...mapState('filters', {
                GetTotalResults: 'totalResults',
                originalQuery: 'query',
            }),

            ...mapGetters('filters', [
                'activeFilters',
            ]),
        },
        mounted() {
            const columnDefs = [
                {
                    targets: '_all',
                    defaultContent: '-',
                },
            ];

            this.query = {
                ...this.query,
                ...this.originalQuery,
            };

            this.columns.forEach((column, index) => {
                switch (column.renders) {
                    case 'badge': {
                        columnDefs.push({
                            targets: index,
                            render: data => `
                                <span class="badge badge-primary status-${data}">
                                    ${this.$t(`general.status.${data}`)}
                                </span>
                            `,
                        });
                        break;
                    }

                    case 'boolean': {
                        columnDefs.push({
                            targets: index,
                            render: data => `<span class="dot dot-${data ? 'primary': 'default'}"></span>`
                        });
                        break;
                    }

                    case 'button-detail': {
                        columnDefs.push({
                            targets: index,
                            render: (data, type, row) =>
                                `
                                    <a
                                        href="${this.link.replace(/%([^%]+)%/, (match, p1) => row[p1])}"
                                        class="button button-gray"
                                    >
                                        <span class="ff-icon-arrow-right m-0"></span>
                                    </a>
                                `
                        });
                        break;
                    }

                    case 'file_size': {
                        columnDefs.push({
                            targets: index,
                            render: data => filesize(data),
                        });
                        break;
                    }

                    case 'link': {
                        columnDefs.push({
                            targets: index,
                            render: (data, type, row) => {
                                let value = data;
                                if (!value) {
                                    value = '-';
                                }
                                return `
                                    <a
                                        class="dataTables_link"
                                        href="${this.link.replace(/%([^%]+)%/, (match, p1) => row[p1])}"
                                    >
                                        ${value}
                                    </a>
                                `;
                            },
                        });
                        break;
                    }

                    case 'datetime': {
                        columnDefs.push({
                            targets: index,
                            render: data => data ? new Date(data).toLocaleString() : '-',
                        });
                        break;
                    }

                    default:
                }
            });

            this.table = $(this.$refs.table);
            this.dataTable = this.table.DataTable({
                ajax: {
                    url: this.ajax,
                    type: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': window.Laravel.csrfToken,
                    },
                },
                lengthMenu: [10, 25, 50, 100, 250],
                cache: false,
                searching: true,
                responsive: true,
                stateSave: true,
                serverSide: true,
                dom: 'lf<"scrollable-table"t>ip',
                order: [this.order],
                columnDefs,
                columns: this.columns,
                rowId: 'id',
                select: {
                    style: 'multi',
                },
                displayStart: this.query.page * (this.query.length ? this.query.length : this.pageLength),
                pageLength: this.query.length ? this.query.length : this.pageLength,
                info: false,
                language: {
                    decimal: '',
                    emptyTable: this.language?.empty_table ?? 'Geen resultaten',
                    info: this.language?.info ?? '_START_ tot _END_ van _TOTAL_ bestanden',
                    infoEmpty: this.language?.infoEmpty ?? 'Geen resultaten beschikbaar',
                    infoFiltered: this.language?.infoFiltered ?? 'gefilterd uit _MAX_ bestanden',
                    infoPostFix: '',
                    thousands: ',',
                    lengthMenu: this.language?.lengthMenu
                        ?? '_MENU_ <span class="hidden-until-medium">resultaten weergeven</span>',
                    loadingRecords: this.language?.loadingRecords ?? 'Laden...',
                    processing: this.language?.processing ?? 'Bezig...',
                    search: this.language?.search ?? '',
                    searchPlaceholder: this.language?.search ?? 'Zoeken',
                    zeroRecords: this.language?.zeroRecords ?? 'Geen resultaten',
                    paginate: {
                        first: this.language?.paginate?.first ?? 'Eerste',
                        last: this.language?.paginate?.last ?? 'Laatste',
                        next: this.language?.paginate?.next ?? 'Volgende',
                        previous: this.language?.paginate?.previous ?? 'Vorige',
                    },
                    aria: {
                        sortAscending: this.language?.aria?.sortAscending ?? 'activeer om kolom oplopend te sorteren',
                        sortDescending: this.language?.aria?.sortDescending ?? 'activeer om kolom aflopend te sorteren',
                    },
                },
                rowCallback: (row, data) => {
                    if (this.unreadRowClass && data.seen_at === null) $(row).addClass('dataTables_unread-row');
                    if (data.status) $(row).addClass(data.status);
                },
                fnDrawCallback: (oSettings) => {
                    tippy('[data-tippy-content]', {
                        arrow: true,
                    });

                    const pagination = $(oSettings.nTableWrapper).find('.dataTables_paginate');
                    oSettings._iDisplayLength > oSettings.fnRecordsDisplay() ? pagination.hide() : pagination.show();
                },
            });

            if (this.rowClickable) {
                this.table.on('click', 'tbody tr', (e) => {
                    const dataRow = e.currentTarget;
                    let data = this.dataTable.row(dataRow).data();

                    window.location = this.link.replace('%id%', data.id);
                });
            }

            this.table.on('page.dt', () => {
                eventHub.$emit('pageChange', this.dataTable.page.info().page);
            });

            this.table.on('length.dt', (e, settings, pageLength) => {
                eventHub.$emit('pageLengthChange', pageLength);
            });

            this.table.on('preXhr.dt', () => {
                this.isLoading = true;
            });

            this.table.on('xhr.dt', (e, settings) => {
                if (settings.jqXHR.statusText === 'abort') return;

                this.isLoading = false;
                this.setTotalResults(settings.json.recordsFiltered);
            });

            this.table.on('click', '.js-confirm-modal', async (e) => {
                e.preventDefault();

                const $curr = $(e.currentTarget);

                await this.confirmModal($curr.attr('data-confirm-text'));
                $curr.submit();
            });

            this.table.on('click', '.js-set-modal', async (e) => {
                e.preventDefault();

                const $curr = $(e.currentTarget);
                const $callbackId = $curr.attr('data-callback-id');
                const $callbackRoute = $curr.attr('data-callback-route');

                this.setModal($curr.attr('data-modal'));

                if ($callbackRoute) {
                    await this.$nextTick();

                    eventHub.$emit('ajaxFormLoad', {
                        id: $callbackId,
                        route: $callbackRoute,
                    });
                }
            });

            eventHub.$on('tableReload', () => {
                this.dataTable.ajax.reload();
            });

            eventHub.$on('updateFilter', ({ column, value }) => {
                if (column === 'search') {
                    this.dataTable.search(value);
                }

                this.dataTable.column(column).search(value);
                this.drawTable();
            });
        },
        methods: {
            ...mapMutations({
                setModal: 'modal/setModal',
                setTotalResults: 'filters/setTotalResults',
            }),

            drawTable() {
                clearTimeout(this.filterDebounce);

                this.filterDebounce = setTimeout(() => {
                    this.dataTable.draw();
                }, 500);
            },
        },
    };
</script>

<style lang="scss">
    .table-responsive {
        @include card;
        position: relative;
        margin-top: 24px;
    }

    .spinner-holder {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .dataTables {
        &_length,
        &_filter {
            padding: 0 16px;

            @include respond-to(md) {
                width: 50%;
                padding: 0 24px 16px;
            }

            .table-custom-search & {
                display: none;
            }
        }

        &_wrapper {
            position: relative;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;

            .scrollable-table {
                width: 100%;
            }
        }
    }

    .dot {
        display: block;
        width: 8px;
        height: 8px;
        background-color: $gray;
        border-radius: 50%;

        &-primary {
            background-color: $primary;
        }
    }

    .legend-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        font-size: 10px;

        &.ff-icon-edit {
            font-size: 8px;
        }
    }

    .narrow {
        text-align: right;
    }

    .dataTables_paginate {
        margin: 24px 0;
    }

    .paginate_button {
        display: inline-block;
        width: 28px;
        height: 28px;
        margin: 0 4px;
        font-size: 16px;
        font-weight: bold;
        line-height: 28px;
        text-align: center;
        text-decoration: none;
        background-color: #fff;
        border-radius: $radius;
        transition: 0.3s;
        transition-property: background, color;

        &-icon {
            line-height: 28px;
        }

        &.next,
        &.previous {
            width: auto;
            height: 28px;
            padding: 0 14px;
            line-height: 28px;
        }

        &.disabled {
            opacity: 0.5;
        }
    }
</style>
